import { useEffect, useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import posthog from "posthog-js";
import { isVpcMode } from "@/utils/vpcMode";
import { StudioPageLoadTracker, type StudioLoadMilestone, type StudioPageReady } from "./studioPageLoad";

const tracker = new StudioPageLoadTracker();

/** Mounted above lazy routes, so detail fetches and route loading are included. */
export function useStudioPageLoadNavigation() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const navigation = performance.getEntriesByType("navigation")[0];
    tracker.enter(pathname, performance.now(), navigation ? new URL(navigation.name).pathname : null,
      document.visibilityState === "hidden");
  }, [pathname]);
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") tracker.backgrounded();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);
}

/** Wait for a paint opportunity after the ready canvas has committed. */
const MILESTONE_REPORTING: Record<StudioLoadMilestone, { event: string; logTag: string }> = {
  complete: { event: "dataset_studio_loaded", logTag: "[studio-page-load]" },
  first_usable: { event: "dataset_studio_first_usable", logTag: "[studio-first-usable]" },
};

function useStudioLoadMilestone(ready: boolean, result: StudioPageReady, milestone: StudioLoadMilestone) {
  const { datasetId, cacheHit, eventCount } = result;
  useEffect(() => {
    if (!ready) return;
    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const properties = tracker.ready({ datasetId, cacheHit, eventCount }, performance.now(), milestone);
        if (!properties) return;
        // Observability must not interrupt the Studio. Preserve the existing
        // SDK opt-out settings and the VPC boundary; no new analytics setup.
        try {
          const { event, logTag } = MILESTONE_REPORTING[milestone];
          if (import.meta.env.DEV) console.info(logTag, JSON.stringify(properties));
          if (!isVpcMode()) posthog.capture(event, properties);
        } catch {
          // The page remains usable if an analytics integration fails.
        }
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
    };
  }, [ready, datasetId, cacheHit, eventCount, milestone]);
}

/** Keep the historical complete-readiness metric unchanged. */
export function useStudioPageLoadReady(ready: boolean, result: StudioPageReady) {
  useStudioLoadMilestone(ready, result, "complete");
}

export function useStudioFirstUsable(ready: boolean, result: StudioPageReady) {
  useStudioLoadMilestone(ready, result, "first_usable");
}
